import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {ActionList, ActionMenu, Button, Checkbox, FormControl, Portal, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'relay-runtime'

import {blockUserFromOrganization} from '../../mutations/block-user-from-organization-mutation'
import styles from './BlockUserFromOrgDialog.module.css'
import {getHideOptionLabel, type HIDE_OPTIONS, HideCommentActionItems} from './HideCommentActions'

type BlockUserFromOrgDialogBaseProps = {
  organization: {login: string}
  contentId: string
  contentAuthor: {login: string}
  contentUrl: string
  onClose: () => void
}

type BlockUserFromOrgDialogWithRelayProps = BlockUserFromOrgDialogBaseProps & {
  organization: {login: string; id: string}
  contentAuthor: {login: string; id: string}
  onBlock?: undefined
}

type BlockUserFromOrgDialogWithCallbackProps = BlockUserFromOrgDialogBaseProps & {
  /**
   * Callback function to handle the block action instead of Relay.
   * The footer renders without Relay, allowing components outside a Relay environment to use this dialog.
   */
  onBlock: (
    duration: string,
    hideComment: boolean,
    hiddenReason: HIDE_OPTIONS | undefined,
    organizationLogin: string,
    notifyBlockedUser: boolean,
    userLogin: string,
    note?: string,
    closeContent?: boolean,
  ) => void
}

export type BlockUserFromOrgDialogProps = BlockUserFromOrgDialogWithRelayProps | BlockUserFromOrgDialogWithCallbackProps

type BlockFromOrganizationDuration = 'ONE_DAY' | 'THREE_DAYS' | 'SEVEN_DAYS' | 'THIRTY_DAYS' | 'INDEFINITE'

export const BLOCKING_DURATION_TO_READABLE_MAP: Record<BlockFromOrganizationDuration, string> = {
  ONE_DAY: 'For 1 day',
  THREE_DAYS: 'For 3 days',
  SEVEN_DAYS: 'For 7 days',
  THIRTY_DAYS: 'For 30 days',
  INDEFINITE: 'Until I unblock them',
} as const

export const BLOCKING_DURATION_TO_DAYS_MAP: Record<BlockFromOrganizationDuration, string> = {
  ONE_DAY: '1',
  THREE_DAYS: '3',
  SEVEN_DAYS: '7',
  THIRTY_DAYS: '30',
  INDEFINITE: '0', // Indefinite means no specific duration, so we use '0' to indicate no expiration
} as const

const INITIAL_SELECTED_BLOCKING_DURATION = 'INDEFINITE'

export const BlockUserFromOrgDialog = (props: BlockUserFromOrgDialogProps) => {
  const {onBlock, onClose} = props
  const [selectedBlockingDuration, setSelectedBlockingDuration] = useState<BlockFromOrganizationDuration>(
    INITIAL_SELECTED_BLOCKING_DURATION,
  )

  // Default to true for notifying the blocked user
  const [notifyBlockedUser, setNotifyBlockedUser] = useState<boolean>(true)
  const [hideComment, setHideComment] = useState<boolean>(false)
  const [commentHideOption, setCommentHideOption] = useState<HIDE_OPTIONS | undefined>(undefined)
  const [note, setNote] = useState('')
  const [closeContent, setCloseContent] = useState(false)
  const userToBlock = props.contentAuthor.login
  const noteValue = note.length > 0 ? note : undefined
  const closeContentEnabled = isFeatureEnabled('block_user_close_content')

  return (
    <Portal>
      <Dialog
        title={`Block ${props.contentAuthor.login} from ${props.organization.login}`}
        renderFooter={() =>
          onBlock ? (
            <BlockUserFromOrgDialogFooterWithoutRelay
              {...props}
              onBlock={onBlock}
              onClose={onClose}
              notifyBlockedUser={notifyBlockedUser}
              hideComment={hideComment}
              duration={selectedBlockingDuration}
              hiddenReason={commentHideOption}
              note={noteValue}
              closeContent={closeContent}
            />
          ) : (
            <BlockUserFromOrgDialogFooterWithController
              {...props}
              onClose={onClose}
              notifyBlockedUser={notifyBlockedUser}
              hideComment={hideComment}
              duration={selectedBlockingDuration}
              hiddenReason={commentHideOption}
              note={noteValue}
              closeContent={closeContent}
            />
          )
        }
        width="xlarge"
        height="auto"
        onClose={onClose}
        className={styles.BlockUserDialog}
      >
        <div className={styles.BlockUserContainer}>
          <div className={styles.BlockUserFormRow}>
            <div>
              <span>
                Blocking a user prevents them from interacting with repositories of this organization, such as opening
                or commenting on pull requests or issues. Learn more about{' '}
                <a href="https://docs.github.com/communities/maintaining-your-safety-on-github/blocking-a-user-from-your-organization">
                  blocking a user.
                </a>
              </span>
            </div>
          </div>
          <div className={styles.BlockUserFormRow}>
            <ActionMenu>
              <ActionMenu.Button>
                Block a user: {BLOCKING_DURATION_TO_READABLE_MAP[selectedBlockingDuration]}
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList selectionVariant="single">
                  <ActionList.Group>
                    <ActionList.GroupHeading>Block options</ActionList.GroupHeading>
                    {(Object.keys(BLOCKING_DURATION_TO_READABLE_MAP) as BlockFromOrganizationDuration[]).map(option => (
                      <ActionList.Item
                        key={option}
                        onSelect={() => {
                          setSelectedBlockingDuration(option)
                        }}
                        selected={option === selectedBlockingDuration}
                      >
                        {BLOCKING_DURATION_TO_READABLE_MAP[option]}
                      </ActionList.Item>
                    ))}
                  </ActionList.Group>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
          <FormControl className={styles.BlockUserFormControl}>
            <FormControl.Label>Add an optional note</FormControl.Label>
            <Textarea
              value={note}
              onChange={event => setNote(event.target.value)}
              rows={3}
              maxLength={250}
              characterLimit={250}
              block
              resize="vertical"
              name="note"
            />
            <FormControl.Caption>
              Maximum 250 characters. Please don&apos;t include any personal information such as legal names or email
              addresses. Markdown is supported. All org admins and moderators can see this note.
            </FormControl.Caption>
          </FormControl>
          <DialogDivider />
          <h4>More options</h4>
          {closeContentEnabled && (
            <FormControl className={styles.BlockUserFormControl}>
              <Checkbox checked={closeContent} onChange={event => setCloseContent(event.target.checked)} />
              <FormControl.Label>
                Close all open issues, pull requests, and discussions opened by this user
              </FormControl.Label>
              <FormControl.Caption>
                Content in repositories owned by this organization where you have write access will be closed.
              </FormControl.Caption>
            </FormControl>
          )}
          <FormControl className={styles.BlockUserFormControl}>
            <Checkbox
              checked={notifyBlockedUser}
              onChange={() => setNotifyBlockedUser(prev => !prev)}
              aria-checked={notifyBlockedUser}
            />
            <FormControl.Label>Send user a notification and show activity in timeline</FormControl.Label>
            <FormControl.Caption>
              <span className={styles.BlockUserHelpText}>
                You can notify <b>{userToBlock}</b> as to why they&apos;re being blocked. A public timeline entry will
                show that this user was blocked. They will receive an email notification and a notice when they try to
                comment with the following message:
                <div className={styles.BlockUserIndentedContainer}>
                  <blockquote>
                    A maintainer of the @{props.organization.login} organization has blocked you because of{' '}
                    <a href={props.contentUrl}>this content</a>. For more information please see{' '}
                    <a href="https://docs.github.com/site-policy/github-terms/github-community-guidelines">
                      the community guidelines
                    </a>
                    .
                  </blockquote>
                </div>
              </span>
            </FormControl.Caption>
          </FormControl>
          <FormControl>
            <Checkbox checked={hideComment} onChange={() => setHideComment(prev => !prev)} aria-checked={hideComment} />
            <FormControl.Label>Hide this user&apos;s comments</FormControl.Label>
            <FormControl.Caption>
              <span className={styles.BlockUserHelpText}>
                All of their comments in this organization{' '}
                <a href="https://docs.github.com/communities/moderating-comments-and-conversations/managing-disruptive-comments#hiding-a-comment">
                  will be hidden
                </a>{' '}
                to all users and will display a reason.
              </span>
            </FormControl.Caption>
          </FormControl>
          <div className={styles.BlockUserLeftPadding}>
            <ActionMenu>
              <ActionMenu.Button>
                {(commentHideOption && getHideOptionLabel(commentHideOption)) ?? 'Choose a reason'}
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList selectionVariant="single">
                  <HideCommentActionItems onSelect={setCommentHideOption} />
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      </Dialog>
    </Portal>
  )
}

const DialogDivider = () => {
  return <div className={styles.BlockUserDivider} />
}

type BlockUserFromOrgDialogFooterBaseProps = {
  notifyBlockedUser: boolean
  hideComment: boolean
  duration: BlockFromOrganizationDuration
  hiddenReason: HIDE_OPTIONS | undefined
  note?: string
  closeContent: boolean
}

type BlockUserFromOrgDialogFooterWithControllerProps = BlockUserFromOrgDialogFooterBaseProps &
  BlockUserFromOrgDialogWithRelayProps

type BlockUserFromOrgDialogFooterWithoutRelayProps = BlockUserFromOrgDialogFooterBaseProps &
  BlockUserFromOrgDialogWithCallbackProps

const BlockUserFromOrgDialogFooterWithController = ({
  organization,
  contentAuthor,
  contentId,
  notifyBlockedUser,
  hideComment,
  duration,
  hiddenReason,
  note,
  closeContent,
  onClose,
}: BlockUserFromOrgDialogFooterWithControllerProps) => {
  const environment = useRelayEnvironment()

  const onSubmit = useCallback(() => {
    blockUserFromOrganization({
      input: {
        contentId,
        duration: BLOCKING_DURATION_TO_DAYS_MAP[duration] ?? '0',
        hiddenReason: hideComment ? hiddenReason : undefined,
        closeContent,
        organizationLogin: organization.login,
        notifyBlockedUser,
        note,
        shouldHideComment: hideComment,
        userLogin: contentAuthor.login,
      },
      onError: noop,
      onCompleted: () =>
        commitLocalUpdate(environment, store => {
          const contentObject = store.get(contentId)
          contentObject?.setValue(false, 'pendingUnblock')
          contentObject?.setValue(true, 'pendingBlock')
          if (hiddenReason) {
            contentObject?.setValue(hiddenReason, 'pendingMinimizeReason')
          }
        }),
    })

    onClose()
  }, [
    environment,
    organization.login,
    contentId,
    contentAuthor.login,
    duration,
    hideComment,
    hiddenReason,
    closeContent,
    notifyBlockedUser,
    note,
    onClose,
  ])

  return (
    <BlockUserFromOrgDialogFooterComponent hideComment={hideComment} hiddenReason={hiddenReason} onSubmit={onSubmit} />
  )
}

const BlockUserFromOrgDialogFooterWithoutRelay = ({
  contentAuthor,
  duration,
  hideComment,
  hiddenReason,
  onBlock,
  onClose,
  organization,
  notifyBlockedUser,
  note,
  closeContent,
}: BlockUserFromOrgDialogFooterWithoutRelayProps) => {
  const onSubmit = () => {
    onBlock(
      BLOCKING_DURATION_TO_DAYS_MAP[duration] ?? '0',
      hideComment,
      hiddenReason,
      organization.login,
      notifyBlockedUser,
      contentAuthor.login,
      note,
      closeContent,
    )
    onClose()
  }

  return (
    <BlockUserFromOrgDialogFooterComponent hideComment={hideComment} hiddenReason={hiddenReason} onSubmit={onSubmit} />
  )
}

interface BlockUserFromOrgDialogFooterComponentProps {
  hideComment: boolean
  hiddenReason: HIDE_OPTIONS | undefined
  onSubmit: () => void
}

const BlockUserFromOrgDialogFooterComponent = ({
  hideComment,
  hiddenReason,
  onSubmit,
}: BlockUserFromOrgDialogFooterComponentProps) => {
  return (
    <Dialog.Footer>
      <Button variant={'danger'} disabled={hideComment && !hiddenReason} onClick={onSubmit} className={styles.Button}>
        Block user from organization
      </Button>
    </Dialog.Footer>
  )
}
